import React from "react";
import { useTranslation } from "react-i18next";
import {
  EmailBuilderAlignmentControl,
  EmailBuilderButtonWidthControl,
} from "../../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import { EmailBuilderSettingsCornerRoundingSection } from "../controls/EmailBuilderSettingsCornerRoundingSection";
import { EmailBuilderSettingsPaddingSection } from "../controls/EmailBuilderSettingsPaddingSection";
import { UrlField } from "../UrlField";
import { SettingSection } from "../controls/SettingSection";
import { ButtonAppearanceSection } from "../ButtonAppearanceSection";
import {
  baselineButtonBorderRadius,
  baselineButtonPadding,
  baselineButtonSize,
} from "../../domain/baselines";
import {
  isCornerRoundingInactive,
  isPaddingInactive,
} from "../settingsSectionActivation";
import { blockHasColumnOverflowInDocument } from "../../domain/column-content-min";
import { selectDocument } from "../../domain/selectors";
import { useEditorStore } from "../../domain/redux/hooks";
import type { ButtonBlock } from "../../domain/types";
import type { BlockSettingsProps } from "./types";
import { EmailBuilderColumnOverflowHint } from "../EmailBuilderColumnOverflowHint";

export function ButtonBlockSettings({
  block,
  patch,
  rowId,
}: BlockSettingsProps & { block: ButtonBlock }) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const defaultRadius = baselineButtonBorderRadius();
  const hasColumnOverflow =
    rowId !== undefined &&
    blockHasColumnOverflowInDocument(document, rowId, block.id);

  return (
    <>
      <SettingSection sectionId="content" title={t("Content")}>
        <UrlField
          label={t("Link")}
          value={block.href}
          onChange={href => patch({ href })}
        />
      </SettingSection>

      <SettingSection sectionId="layout" title={t("Layout")}>
        <EmailBuilderButtonWidthControl
          fullWidth={block.fullWidth}
          fullWidthMobile={block.fullWidthMobile}
          onChange={widthPatch => patch(widthPatch)}
        />
        <EmailBuilderAlignmentControl
          value={block.align}
          onChange={align => patch({ align })}
        />
        {hasColumnOverflow ? <EmailBuilderColumnOverflowHint /> : null}
      </SettingSection>

      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(block.padding),
          onAdd: () => patch({ padding: { ...baselineButtonPadding() } }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Button padding (px)")}
          value={block.padding}
          defaultValue={baselineButtonPadding()}
          onChange={padding => {
            patch({ padding: { ...padding } });
          }}
        />
      </SettingSection>

      <SettingSection
        sectionId="corner-rounding"
        title={t("Corner rounding")}
        activation={{
          mode: "optional",
          isActive: !isCornerRoundingInactive(block.borderRadius),
          onAdd: () => patch({ borderRadius: { ...baselineButtonBorderRadius() } }),
        }}
      >
        <EmailBuilderSettingsCornerRoundingSection
          value={block.borderRadius ?? defaultRadius}
          onChange={borderRadius => {
            patch({
              borderRadius: {
                topLeft: borderRadius.topLeft,
                topRight: borderRadius.topRight,
                bottomRight: borderRadius.bottomRight,
                bottomLeft: borderRadius.bottomLeft,
              },
            });
          }}
        />
      </SettingSection>

      <SettingSection sectionId="appearance" title={t("Appearance")}>
        <ButtonAppearanceSection
          value={block}
          sizeDefault={baselineButtonSize()}
          patch={patch}
        />
      </SettingSection>
    </>
  );
}
